import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Film, BarChart3, AlertTriangle, CheckCircle2, Clock, ArrowRight } from 'lucide-react';

const PIPELINE_STAGES = ['storyboard', 'layout', 'animation', 'lighting', 'compositing', 'final'];
const STAGE_COLORS = {
  storyboard: '#8b5cf6', layout: '#06b6d4', animation: '#3b82f6',
  lighting: '#f59e0b', compositing: '#10b981', final: '#ec4899'
};

function PipelineBar({ stage, data }) {
  const pct = data.progress || 0;
  const color = STAGE_COLORS[stage] || '#3b82f6';
  return (
    <div className="flex items-center gap-3" data-testid={`pipeline-stage-${stage}`}>
      <span className="text-[11px] text-zinc-400 w-24 capitalize font-medium">{stage}</span>
      <div className="flex-1 h-6 bg-zinc-800 rounded-sm overflow-hidden relative">
        <div className="h-full rounded-sm transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-mono text-white mix-blend-difference">{pct}%</span>
      </div>
      <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-500 w-32">
        <span className="text-emerald-400">{data.completed}</span>/
        <span>{data.total}</span>
        {data.blocked > 0 && <span className="text-red-400">({data.blocked} blocked)</span>}
      </div>
    </div>
  );
}

export default function ProductionDashboard() {
  const { api } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    api.get('/projects').then(r => {
      setProjects(r.data);
      if (r.data.length > 0) setSelectedProject(r.data[0]);
    });
  }, [api]);

  useEffect(() => {
    if (!selectedProject) return;
    Promise.all([
      api.get(`/pipeline/health/${selectedProject.id}`),
      api.get(`/tasks?project_id=${selectedProject.id}`),
    ]).then(([p, t]) => {
      setPipeline(p.data);
      setTasks(t.data);
    });
  }, [selectedProject, api]);

  return (
    <div className="space-y-6" data-testid="production-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Production Pipeline</h1>
          <p className="text-xs text-zinc-500 mt-1">Track shot progress across all stages</p>
        </div>
        {projects.length > 0 && (
          <select
            data-testid="project-selector"
            value={selectedProject?.id || ''}
            onChange={e => setSelectedProject(projects.find(p => p.id === e.target.value))}
            className="h-8 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-3 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          >
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>

      {pipeline && (
        <>
          {/* Overall Progress */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-heading font-semibold text-zinc-300 uppercase tracking-wider">Pipeline Health</h2>
              <span className="font-mono text-lg font-bold" style={{ color: pipeline.overall_progress >= 50 ? '#10b981' : '#f59e0b' }}>
                {pipeline.overall_progress}%
              </span>
            </div>
            <div className="space-y-2.5">
              {PIPELINE_STAGES.map(stage => (
                pipeline.stages[stage] && <PipelineBar key={stage} stage={stage} data={pipeline.stages[stage]} />
              ))}
            </div>
            <div className="mt-4 flex items-center gap-4 text-[10px] text-zinc-600">
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> {pipeline.completed_tasks} completed</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-blue-400" /> {pipeline.total_tasks - pipeline.completed_tasks} remaining</span>
            </div>
          </div>

          {/* Task Table */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">All Tasks</h3>
              <span className="text-[10px] font-mono text-zinc-600">{tasks.length} tasks</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="tasks-table">
                <thead>
                  <tr className="border-b border-zinc-800/50 text-[10px] text-zinc-500 uppercase tracking-wider">
                    <th className="text-left px-4 py-2 font-medium">Task</th>
                    <th className="text-left px-4 py-2 font-medium">Type</th>
                    <th className="text-left px-4 py-2 font-medium">Assignee</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-left px-4 py-2 font-medium">Progress</th>
                    <th className="text-left px-4 py-2 font-medium">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/30">
                  {tasks.slice(0, 20).map(task => (
                    <tr key={task.id} className="hover:bg-zinc-800/20 transition-colors" data-testid={`task-row-${task.id}`}>
                      <td className="px-4 py-2 text-xs text-zinc-300">{task.name}</td>
                      <td className="px-4 py-2">
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ backgroundColor: `${STAGE_COLORS[task.task_type]}20`, color: STAGE_COLORS[task.task_type] }}>
                          {task.task_type}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-zinc-400">{task.assignee_name}</td>
                      <td className="px-4 py-2"><span className={`status-${task.status} px-1.5 py-0.5 rounded-full text-[9px] font-medium`}>{task.status?.replace('_', ' ')}</span></td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${task.progress}%` }} />
                          </div>
                          <span className="text-[10px] font-mono text-zinc-500">{task.progress}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-[10px] font-mono text-zinc-500">{task.rate_amount?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
